/*
  The Home Screen icon badge — `navigator.setAppBadge()`.

  Two callers set this badge, and they deliberately do not coordinate:

  - This module, whenever the app is open and the unread count changes.
  - The service worker's `push` handler, from `badgeCount` in the payload,
    which is the ONLY one that can run while the app is closed — and the
    closed case is the whole point of a Home Screen badge.

  They can therefore disagree for as long as the app stays shut (a
  notification read on another device, say). That is self-correcting: the
  next thing to happen in either place writes the whole count, never a
  delta, so the badge converges on the next push or the next app open.

  Every call is feature-detected AND wrapped, because both halves fail
  independently:

  - `setAppBadge` is absent entirely on Firefox and on iOS Safari in a
    browser tab, so the `in` check does the work there.
  - Where it EXISTS it can still reject at call time — iOS only honours it
    for an app installed to the Home Screen with notification permission
    granted, and rejects with NotAllowedError otherwise. A rejected promise
    with no catch is an unhandled rejection, which this app's error overlay
    would surface to the user over a decoration.

  A badge is a nicety; nothing here may ever throw into a render or a push
  handler. So every function resolves, and reports whether it landed only
  for callers that care (the tests do; the app does not).
*/

type BadgeNavigator = Navigator & {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

export function isBadgingSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof (navigator as BadgeNavigator).setAppBadge === 'function'
  );
}

/*
  Zero is not a small badge — per spec `setAppBadge(0)` CLEARS the badge, the
  same as clearAppBadge(). Routing 0 explicitly through clearAppBadge keeps
  that from resting on how a given engine reads the spec, and keeps the
  intent legible at the call site.
*/
export async function setAppBadge(count: number): Promise<boolean> {
  if (!isBadgingSupported()) return false;

  const safe = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  if (safe === 0) return clearAppBadge();

  try {
    await (navigator as BadgeNavigator).setAppBadge!(safe);
    return true;
  } catch {
    // iOS rejects unless installed to the Home Screen with permission granted.
    return false;
  }
}

export async function clearAppBadge(): Promise<boolean> {
  const nav = navigator as BadgeNavigator;
  if (typeof nav?.clearAppBadge !== 'function') return false;

  try {
    await nav.clearAppBadge();
    return true;
  } catch {
    return false;
  }
}

/**
 * Take the number off the icon because someone SIGNED OUT on purpose.
 *
 * Not when a session lapses — an idle or expired session is the same person
 * with the same unread notifications (see hooks/use-app-badge.ts). This is for
 * the case where a count left on the icon would advertise one account's
 * pending work to the next person who picks the device up. Call it from the
 * deliberate sign-out path only.
 */
export function clearBadgeForSignOut(): Promise<boolean> {
  return clearAppBadge();
}
